import {
  Ticket,
  MessageSquare,
  CheckCircle,
  Loader2,
  Send,
  X,
  ChevronRight,
  Search,
  Star,
  Plus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  czytajPoTerminie,
  czytajStany,
  czytajZestawienie,
  ETYKIETY_STANU,
  RejestracjaZgloszenia,
  ZestawienieReakcji,
  type ListaPoTerminie,
  type StanReakcji,
  type Zestawienie,
} from '../../components/support-access/CzasReakcji';
import { KartaOpiekuna } from '../../components/support-access/KartaOpiekuna';
import { supabase } from '../../lib/supabase';
import { usunZgloszenie } from '../../lib/usuwanieZgloszenia';
import { useAuth } from '../../providers/AuthProvider';

interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_response' | 'resolved' | 'closed';
  category: string;
  created_at: string;
  updated_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
  partner_name?: string;
  organization_name?: string;
  messages_count?: number;
}

interface TicketMessage {
  id: string;
  user_id: string;
  message: string;
  is_internal: boolean;
  created_at: string;
  user_name?: string;
  is_partner?: boolean;
}

const priorityColors = {
  low: 'bg-aurora-bg-subtle text-aurora-muted',
  normal: 'bg-blue-500/20 text-blue-400',
  high: 'bg-amber-500/20 text-amber-400',
  urgent: 'bg-red-500/20 text-red-400',
};

const statusColors = {
  open: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-amber-500/20 text-amber-400',
  waiting_response: 'bg-purple-500/20 text-purple-400',
  resolved: 'bg-emerald-500/20 text-emerald-400',
  closed: 'bg-aurora-bg-subtle text-aurora-muted',
};

const statusLabels = {
  open: 'Otwarte',
  in_progress: 'W trakcie',
  waiting_response: 'Oczekuje odpowiedzi',
  resolved: 'Rozwiązane',
  closed: 'Zamknięte',
};

const priorityLabels = {
  low: 'Niski',
  normal: 'Normalny',
  high: 'Wysoki',
  urgent: 'Pilny',
};

const categoryLabels: Record<string, string> = {
  general: 'Ogólne',
  technical: 'Techniczne',
  billing: 'Rozliczenia',
  feature_request: 'Prośba o funkcję',
  bug_report: 'Zgłoszenie błędu',
  other: 'Inne',
};

export function SupportPage() {
  const location = useLocation();
  const { user, currentOrganization } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [search, setSearch] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(5);
  const [ratingFeedback, setRatingFeedback] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
  });
  const [creatingTicket, setCreatingTicket] = useState(false);
  // Stan reakcji z widoku `wsparcie_reakcje` (migracja 858) — liczy baza.
  const [zestawienie, setZestawienie] = useState<Zestawienie | null>(null);
  const [poTerminie, setPoTerminie] = useState<ListaPoTerminie | null>(null);
  const [stanReakcji, setStanReakcji] = useState<Map<string, StanReakcji>>(new Map());
  const [ladowanieReakcji, setLadowanieReakcji] = useState(true);
  const [bladReakcji, setBladReakcji] = useState<string | null>(null);
  const [odswiezenie, setOdswiezenie] = useState(0);
  const [odswiezReakcje, setOdswiezReakcje] = useState(0);

  // Detect panel context from URL
  const isSuperAdmin = location.pathname.startsWith('/super-admin');
  const isPartner = location.pathname.startsWith('/partner');
  const isAdmin = !isSuperAdmin && !isPartner;

  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      // For admin panel, require organization
      if (isAdmin && !currentOrganization?.organization_id) return;

      setIsLoading(true);
      try {
        let query = supabase
          .from('support_tickets')
          .select(
            `
            *,
            partner:organizations!support_tickets_partner_id_fkey(name),
            organization:organizations!support_tickets_organization_id_fkey(name)
          `
          )
          .order('created_at', { ascending: false });

        // Apply filter based on panel context
        if (isSuperAdmin) {
          // Super Admin sees ALL tickets
        } else if (isPartner && user?.id) {
          // Partner sees tickets for their clients
          const { data: partnerOrg } = await supabase
            .from('user_organizations')
            .select('organization_id')
            .eq('user_id', user.id)
            .single();

          if (partnerOrg) {
            query = query.eq('partner_id', partnerOrg.organization_id);
          }
        } else if (currentOrganization?.organization_id) {
          // Admin sees their organization's tickets
          query = query.eq('organization_id', currentOrganization.organization_id);
        }

        const { data, error } = await query;

        if (error) throw error;

        const mappedTickets = (data || []).map((t: Record<string, unknown>) => ({
          ...t,
          partner_name: (t.partner as { name?: string } | null)?.name,
          organization_name: (t.organization as { name?: string } | null)?.name,
        })) as SupportTicket[];

        setTickets(mappedTickets);
      } catch (err) {
        console.error('Error fetching tickets:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, [
    currentOrganization?.organization_id,
    isSuperAdmin,
    isPartner,
    isAdmin,
    user?.id,
    odswiezenie,
  ]);

  useEffect(() => {
    if (isAdmin && !currentOrganization?.organization_id) return;
    setLadowanieReakcji(true);
    const org = isAdmin ? currentOrganization?.organization_id : undefined;
    Promise.all([czytajZestawienie(org), czytajPoTerminie(org)])
      .then(([z, p]) => {
        setZestawienie(z);
        setPoTerminie(p);
        setBladReakcji(null);
      })
      .catch((e: Error) => setBladReakcji(e.message))
      .finally(() => setLadowanieReakcji(false));
  }, [currentOrganization?.organization_id, isAdmin, odswiezenie, odswiezReakcje]);

  // Odznaki stanu przy zgłoszeniach z listy — dla dokładnie tych zgłoszeń.
  useEffect(() => {
    const ids = tickets.map((t) => t.id);
    if (ids.length === 0) {
      setStanReakcji(new Map());
      return;
    }
    czytajStany(ids)
      .then(setStanReakcji)
      .catch(() => setStanReakcji(new Map()));
  }, [tickets, odswiezReakcje]);

  // Fetch messages for selected ticket
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedTicket) return;

      setLoadingMessages(true);
      try {
        const { data, error } = await supabase
          .from('ticket_messages')
          .select('*')
          .eq('ticket_id', selectedTicket.id)
          .eq('is_internal', false)
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Get user info for messages
        const userIds = [...new Set((data || []).map((m: { user_id: string }) => m.user_id))];
        // Author names come from public.users (there is no `profiles` table —
        // the previous query silently failed and every author fell back to
        // "Użytkownik"). public.users has id + full_name.
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name')
          .in('id', userIds);

        const userMap = new Map(
          (users || []).map((u: { id: string; full_name?: string }) => [u.id, u.full_name])
        );

        // Check which users are from partner organization
        const partnerUserIds = selectedTicket.partner_name
          ? await (async () => {
              const { data: orgData } = await supabase
                .from('organizations')
                .select('id')
                .eq('name', selectedTicket.partner_name)
                .single();

              if (!orgData) return new Set<string>();

              const { data: partnerUsers } = await supabase
                .from('user_organizations')
                .select('user_id')
                .eq('organization_id', orgData.id);

              return new Set((partnerUsers || []).map((u: { user_id: string }) => u.user_id));
            })()
          : new Set<string>();

        const mappedMessages = (data || []).map((m: Record<string, unknown>) => ({
          ...m,
          user_name: userMap.get(m.user_id as string) || 'Użytkownik',
          is_partner: partnerUserIds.has(m.user_id as string),
        })) as TicketMessage[];

        setMessages(mappedMessages);
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedTicket]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || !user) return;

    setSendingMessage(true);
    try {
      const { error } = await supabase.from('ticket_messages').insert({
        ticket_id: selectedTicket.id,
        user_id: user.id,
        message: newMessage,
        is_internal: false,
      });

      if (error) throw error;

      // Update ticket status
      await supabase
        .from('support_tickets')
        .update({ status: 'waiting_response' })
        .eq('id', selectedTicket.id);
      // Wiadomość personelu jest Pierwszą reakcją (wyzwalacz, migracja 858).
      setOdswiezReakcje((n) => n + 1);

      // Refresh messages
      const { data } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });

      setMessages((data as TicketMessage[]) || []);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Usuwanie zgłoszenia — wyłącznie kontekst super-admina. RLS przepuszcza
  // DELETE tylko przez politykę `support_tickets_super_admin`, więc dla innych
  // ról przycisku nie ma, a nawet wywołany na siłę DELETE nic nie usunie
  // (i `usunZgloszenie` to wykryje po zerze zwróconych wierszy).
  const handleDeleteTicket = async () => {
    if (!selectedTicket || !isSuperAdmin) return;

    setDeletingTicket(true);
    setDeleteError(null);
    const wynik = await usunZgloszenie(supabase, selectedTicket.id);
    setDeletingTicket(false);

    if (!wynik.ok) {
      setDeleteError(wynik.komunikat);
      return;
    }

    setTickets((prev) => prev.filter((t) => t.id !== selectedTicket.id));
    setSelectedTicket(null);
    setShowDeleteModal(false);
  };

  const handleSubmitRating = async () => {
    if (!selectedTicket || !user || !currentOrganization) return;

    setSubmittingRating(true);
    try {
      // Get partner organization id
      const { data: ticketData } = await supabase
        .from('support_tickets')
        .select('partner_id')
        .eq('id', selectedTicket.id)
        .single();

      if (ticketData?.partner_id) {
        await supabase.from('partner_surveys').insert({
          organization_id: currentOrganization.organization_id,
          partner_id: ticketData.partner_id,
          ticket_id: selectedTicket.id,
          user_id: user.id,
          overall_rating: rating,
          feedback_text: ratingFeedback || null,
          survey_type: 'ticket_resolution',
        });
      }

      // Close the ticket
      await supabase
        .from('support_tickets')
        .update({ status: 'closed', closed_at: new Date().toISOString() })
        .eq('id', selectedTicket.id);

      setShowRatingModal(false);
      setRating(5);
      setRatingFeedback('');
      setSelectedTicket(null);

      // Refresh tickets
      const { data } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });

      setTickets((data as SupportTicket[]) || []);
    } catch (err) {
      console.error('Error submitting rating:', err);
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleCreateTicket = async () => {
    if (
      !newTicketForm.subject.trim() ||
      !newTicketForm.description.trim() ||
      !currentOrganization?.organization_id ||
      !user
    )
      return;

    setCreatingTicket(true);
    try {
      // Get the partner assigned to this organization
      const { data: orgData } = await supabase
        .from('organizations')
        .select('partner_id')
        .eq('id', currentOrganization.organization_id)
        .single();

      const { data: newTicket, error } = await supabase
        .from('support_tickets')
        .insert({
          organization_id: currentOrganization.organization_id,
          partner_id: orgData?.partner_id || null,
          created_by: user.id,
          subject: newTicketForm.subject,
          description: newTicketForm.description,
          category: newTicketForm.category,
          priority: newTicketForm.priority,
          status: 'open',
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh tickets
      const { data } = await supabase
        .from('support_tickets')
        .select(
          `
          *,
          partner:organizations!support_tickets_partner_id_fkey(name)
        `
        )
        .eq('organization_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });

      const mappedTickets = (data || []).map((t: Record<string, unknown>) => ({
        ...t,
        partner_name: (t.partner as { name?: string } | null)?.name,
      })) as SupportTicket[];

      setTickets(mappedTickets);
      setShowNewTicketModal(false);
      setNewTicketForm({
        subject: '',
        description: '',
        category: 'general',
        priority: 'normal',
      });

      // Select the newly created ticket
      if (newTicket) {
        setSelectedTicket(newTicket as SupportTicket);
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
    } finally {
      setCreatingTicket(false);
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    if (filter === 'open' && ['resolved', 'closed'].includes(ticket.status)) return false;
    if (filter === 'resolved' && !['resolved', 'closed'].includes(ticket.status)) return false;
    if (search && !ticket.subject.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Opiekun najemcy + zgoda na wejście na konto (migracja 459).
          Tylko w panelu NAJEMCY: super-admin i partner mają tu listę zgłoszeń
          cudzych organizacji, a zgodę na wejście wydaje wyłącznie klient. */}
      {isAdmin && currentOrganization?.organization_id && (
        <KartaOpiekuna organizationId={currentOrganization.organization_id} />
      )}

      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Ticket className="w-7 h-7 text-amber-400" />
            {isSuperAdmin
              ? 'Wszystkie zgłoszenia'
              : isPartner
                ? 'Zgłoszenia klientów'
                : 'Moje zgłoszenia'}
          </h1>
          <p className="pagehead__sub">
            {isSuperAdmin
              ? 'Przeglądaj wszystkie zgłoszenia w systemie'
              : isPartner
                ? 'Zarządzaj zgłoszeniami swoich klientów'
                : 'Zarządzaj swoimi zgłoszeniami i komunikuj się z partnerem'}
          </p>
        </div>
        {isAdmin && (
          <div className="pagehead__actions">
            <button onClick={() => setShowNewTicketModal(true)} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Nowe zgłoszenie
            </button>
          </div>
        )}
      </div>

      <ZestawienieReakcji
        zestawienie={zestawienie}
        poTerminie={poTerminie}
        ladowanie={ladowanieReakcji}
        blad={bladReakcji}
      />
      {isSuperAdmin && user?.id && (
        <RejestracjaZgloszenia
          uzytkownikId={user.id}
          poZapisie={() => setOdswiezenie((n) => n + 1)}
        />
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="inputwrap flex-1">
          <Search />
          <input
            type="text"
            placeholder="Szukaj zgłoszeń..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {(['all', 'open', 'resolved'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className="chip"
            >
              {f === 'all' ? 'Wszystkie' : f === 'open' ? 'Otwarte' : 'Rozwiązane'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-1 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 card">
              <Ticket className="w-12 h-12 text-aurora-subtle mx-auto mb-3" />
              <p className="text-aurora-muted">Brak zgłoszeń</p>
            </div>
          ) : (
            filteredTickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`card card--interactive w-full p-4 transition-all text-left ${
                  selectedTicket?.id === ticket.id ? 'bg-blue-500/10 border-aurora-brand' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-medium text-aurora-text line-clamp-1">{ticket.subject}</h3>
                  <ChevronRight className="w-4 h-4 text-aurora-subtle flex-shrink-0" />
                </div>
                {(isSuperAdmin || isPartner) && ticket.organization_name && (
                  <p className="text-xs text-amber-400 mb-2 truncate">{ticket.organization_name}</p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`badge border-transparent ${statusColors[ticket.status]}`}>
                    {statusLabels[ticket.status]}
                  </span>
                  <span className={`badge border-transparent ${priorityColors[ticket.priority]}`}>
                    {priorityLabels[ticket.priority]}
                  </span>
                  {stanReakcji.get(ticket.id) && (
                    <span
                      className={`badge border-transparent ${ETYKIETY_STANU[stanReakcji.get(ticket.id)!].klasa}`}
                    >
                      {ETYKIETY_STANU[stanReakcji.get(ticket.id)!].tekst}
                    </span>
                  )}
                </div>
                <p className="mono text-xs text-aurora-subtle mt-2">
                  {formatDate(ticket.created_at)}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Ticket Detail */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <div className="card h-full flex flex-col">
              {/* Ticket Header */}
              <div className="p-4 border-b border-aurora-border">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="card__title text-lg text-aurora-text">
                      {selectedTicket.subject}
                    </h2>
                    <p className="text-sm text-aurora-muted mt-1">
                      {categoryLabels[selectedTicket.category]} •{' '}
                      <span className="mono">{formatDate(selectedTicket.created_at)}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`pill ${statusColors[selectedTicket.status]}`}>
                      <span className="pin" />
                      {statusLabels[selectedTicket.status]}
                    </span>
                    {isSuperAdmin && (
                      <button
                        onClick={() => {
                          setDeleteError(null);
                          setShowDeleteModal(true);
                        }}
                        className="iconbtn text-red-400 hover:bg-red-500/10"
                        title="Usuń zgłoszenie"
                        aria-label="Usuń zgłoszenie"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {selectedTicket.status === 'resolved' && (
                      <button
                        onClick={() => setShowRatingModal(true)}
                        className="pill pill--active hover:bg-emerald-500/30 transition-colors"
                      >
                        <Star className="w-3 h-3" />
                        Oceń
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-4 p-3 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md">
                  <p className="text-sm text-aurora-muted">{selectedTicket.description}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-aurora-muted animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-aurora-subtle">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Brak wiadomości</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.is_partner ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-ids-lg ${
                          msg.is_partner
                            ? 'bg-aurora-raised text-aurora-text'
                            : 'bg-blue-500/15 text-aurora-text'
                        }`}
                      >
                        <p className="text-xs font-medium mb-1 opacity-70">
                          {msg.is_partner ? `${selectedTicket.partner_name}` : 'Ty'}
                        </p>
                        <p className="text-sm">{msg.message}</p>
                        <p className="mono text-xs opacity-50 mt-1">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message Input */}
              {!['resolved', 'closed'].includes(selectedTicket.status) && (
                <div className="p-4 border-t border-aurora-border">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Napisz wiadomość..."
                      className="input flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      className="btn btn--primary"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card h-full flex items-center justify-center">
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-aurora-subtle mx-auto mb-3" />
                <p className="text-aurora-muted">Wybierz zgłoszenie, aby zobaczyć szczegóły</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal (super-admin) */}
      {showDeleteModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 p-4 sm:p-6 max-w-xl w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="display text-xl font-bold text-aurora-text flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                Usuń zgłoszenie
              </h2>
              <button onClick={() => setShowDeleteModal(false)} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-aurora-muted">
                Zgłoszenie{' '}
                <span className="font-medium text-aurora-text">
                  „{selectedTicket.subject}&rdquo;
                </span>{' '}
                zostanie usunięte trwale, razem ze wszystkimi wiadomościami. Tej operacji nie da się
                cofnąć.
              </p>

              {deleteError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-ids-md text-sm text-red-400">
                  {deleteError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn--secondary flex-1"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleDeleteTicket}
                  disabled={deletingTicket}
                  className="btn flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                >
                  {deletingTicket ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Usuń trwale
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 p-4 sm:p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="display text-xl font-bold text-aurora-text">Oceń obsługę</h2>
              <button onClick={() => setShowRatingModal(false)} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="text-center">
                <p className="text-aurora-muted mb-4">
                  Jak oceniasz rozwiązanie Twojego zgłoszenia?
                </p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= rating ? 'text-amber-400 fill-amber-400' : 'text-aurora-subtle'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="field__label">Dodatkowy komentarz (opcjonalnie)</label>
                <textarea
                  value={ratingFeedback}
                  onChange={(e) => setRatingFeedback(e.target.value)}
                  placeholder="Podziel się swoją opinią..."
                  rows={3}
                  className="textarea resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="btn btn--secondary flex-1"
                >
                  Pomiń
                </button>
                <button
                  onClick={handleSubmitRating}
                  disabled={submittingRating}
                  className="btn btn--primary flex-1"
                >
                  {submittingRating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Wyślij ocenę
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 p-4 sm:p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="display text-xl font-bold text-aurora-text flex items-center gap-2">
                <Plus className="w-6 h-6 text-amber-400" />
                Nowe zgłoszenie
              </h2>
              <button onClick={() => setShowNewTicketModal(false)} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="field">
                <label className="field__label">Temat *</label>
                <input
                  type="text"
                  value={newTicketForm.subject}
                  onChange={(e) => setNewTicketForm({ ...newTicketForm, subject: e.target.value })}
                  placeholder="Krótki opis problemu..."
                  className="input"
                />
              </div>

              <div className="field">
                <label className="field__label">Opis *</label>
                <textarea
                  value={newTicketForm.description}
                  onChange={(e) =>
                    setNewTicketForm({ ...newTicketForm, description: e.target.value })
                  }
                  placeholder="Opisz szczegółowo swój problem lub pytanie..."
                  rows={4}
                  className="textarea resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="field">
                  <label className="field__label">Kategoria</label>
                  <select
                    value={newTicketForm.category}
                    onChange={(e) =>
                      setNewTicketForm({ ...newTicketForm, category: e.target.value })
                    }
                    className="select"
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label className="field__label">Priorytet</label>
                  <select
                    value={newTicketForm.priority}
                    onChange={(e) =>
                      setNewTicketForm({
                        ...newTicketForm,
                        priority: e.target.value as 'low' | 'normal' | 'high' | 'urgent',
                      })
                    }
                    className="select"
                  >
                    {Object.entries(priorityLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="btn btn--secondary flex-1"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleCreateTicket}
                  disabled={
                    creatingTicket ||
                    !newTicketForm.subject.trim() ||
                    !newTicketForm.description.trim()
                  }
                  className="btn btn--primary flex-1"
                >
                  {creatingTicket ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Utwórz zgłoszenie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
